/** A person named by an identifier elsewhere in the product — **the one place that decides how**.
 *
 * Records name people as UUIDs (rule 4): a risk's owner, a task's owner, a ledger entry's actor.
 * What a *screen* does with that identifier depends on who is reading and on what the screen is for,
 * and this component is that decision so two screens cannot make it differently:
 *
 * - **An Admin may follow it.** The People roster is Admin-only, so the link to `/people/$userId` is
 *   rendered for an Admin and for nobody else. A link a Contributor could click is a page that
 *   predictably refuses them (design rule 6).
 * - **A name is shown only where the screen is allowed to show one.** `showName` is true on every
 *   screen since 2026-11-03, the audit ledger included: an Admin sees what the People screen already
 *   shows them, read from the same query, so this adds no request, and the identifier is on hover.
 *   The chain itself still holds identifiers only (rule 4); only this rendering resolves one.
 * - **No name is ever invented.** Not on the roster (a person of another workspace, or one the
 *   roster no longer lists) reads as the identifier, with the reason beside it, not as "unknown".
 */

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useMe } from "../api/auth";
import { useMembers } from "../api/queries";
import { keepSearch } from "./search";
import own from "./PersonRef.module.css";

/** A person's name, for a screen that must not print an identifier where a reader wants a name (U7).
 *
 * **Only the company Admin can resolve one**: the roster (`GET /members`) is the Admin's, and nothing
 * else in the product turns an identifier into a name for another role. So this returns a name for an
 * Admin and `undefined` for everyone else, who see the screen's own fallback (a short identifier, or
 * the word the screen already used). Giving the other roles names is a decision of its own
 * (ADR-0218, proposed) and is not made by making a screen more helpful. The identifier stays the
 * fact: it is in the element's `title`, and the ledger's payloads still hold identifiers only (rule 4).
 *
 * The roster is the same query People and the Admin tabs already make, so this adds no request that
 * the Admin's session was not already making.
 */
export function useNameOf(): (userId: string | null | undefined) => string | undefined {
  const { data: me } = useMe();
  const roster = useMembers(me?.role === "admin");
  return (userId) => {
    if (!userId) return undefined;
    return roster.data?.find((entry) => entry.user_id === userId)?.display_name;
  };
}

/** What an Admin reads where a name should be and the roster could not be read (G-P9-21). The
 * identifier is **not** the answer: an Admin's screen prints names, and a column of UUIDs because one
 * request failed reads as the product having changed. The failure itself is said once, above the
 * screen (`shell/LookupsFailed`), with its Try again; the identifier is on hover. */
export const NAME_NOT_LOADED = "Name not loaded";

/** `useNameOf`, drawn: the name with the identifier on hover, else `fallback`. */
export function PersonName({
  userId,
  fallback,
}: {
  userId: string | null | undefined;
  fallback: ReactNode;
}) {
  const nameOf = useNameOf();
  const { data: me } = useMe();
  const roster = useMembers(me?.role === "admin");
  const name = nameOf(userId);
  if (userId && name === undefined && roster.error) {
    return (
      <span title={userId} data-testid="person-name-unread" data-person-id={userId}>
        {NAME_NOT_LOADED}
      </span>
    );
  }
  if (!userId || name === undefined) return <>{fallback}</>;
  return (
    <span title={userId} data-testid="person-name" data-person-id={userId}>
      {name}
    </span>
  );
}

export function PersonRef({ userId, showName }: { userId: string; showName: boolean }) {
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const roster = useMembers(isAdmin && showName);
  const member = roster.data?.find((entry) => entry.user_id === userId);
  // A roster that could not be read leaves the identifier unexplained: say the name did not load
  // (the notice above the screen says why), keeping the identifier on hover. A person the roster
  // *did* answer without (another workspace's) keeps the identifier, which is the honest answer.
  const unread = showName && !member && Boolean(roster.error);
  const label = showName && member ? member.display_name : unread ? NAME_NOT_LOADED : userId;

  if (!isAdmin) {
    return (
      <span className={own.id} data-testid="person-ref" data-person-id={userId}>
        {userId}
      </span>
    );
  }
  return (
    <Link
      className={showName && member ? own.name : own.id}
      to="/people/$userId"
      params={{ userId }}
      search={keepSearch}
      title={userId}
      data-testid="person-ref"
      data-person-id={userId}
    >
      {label}
    </Link>
  );
}
